import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import type { BuilderStep } from "@/client/constants/builder-steps";
import { useStepFormResume } from "@/client/providers/step-form";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { isStepComplete } from "./sidebar/step-status";

export const BuilderNavigation = () => {
  const { selector, phase, steps, nextStep, prevStep } = useStepFormResume();

  const resume = useResumeStore((state) => state.resume);

  const { open: openDownload } = useDialog("confirm-download");

  const index = steps.indexOf(selector);

  const isTemplates = selector === "templates";

  const isFinalization = selector === "finalization";

  const isFirstStep = index <= 0;

  const canContinue =
    phase === "intro" ||
    (!isTemplates && !isFinalization && isStepComplete(selector as BuilderStep, resume));

  /*
   * BuilderNavigation est l’unique
   * propriétaire des boutons Retour,
   * Continuer et Terminer mon CV.
   *
   * Les formulaires des rubriques ne
   * doivent pas recréer ces boutons.
   */
  if (isTemplates) {
    return null;
  }

  return (
    <nav
      aria-label="Navigation entre les rubriques"
      className={cn(
        "flex h-[68px] shrink-0",
        "items-center justify-between gap-3",
        "border-t border-indigo-100",
        "bg-white/95 px-4",
        "backdrop-blur-sm",
        "sm:px-6 lg:px-8",
      )}
    >
      <button
        type="button"
        disabled={isFirstStep}
        onClick={prevStep}
        className={cn(
          "inline-flex h-11 items-center gap-2",
          "rounded-xl border border-indigo-600",
          "px-4 text-xs font-bold text-indigo-800",
          "transition-colors hover:bg-indigo-50",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-35",
          "sm:px-5",
        )}
      >
        <ArrowLeftIcon aria-hidden size={17} weight="bold" />
        Retour
      </button>

      {!isFinalization && (
        <button
          type="button"
          disabled={!canContinue}
          onClick={nextStep}
          className={cn(
            "inline-flex h-11 min-w-36",
            "items-center justify-center gap-3",
            "rounded-xl px-5",
            "bg-gradient-to-r",
            "from-indigo-600 via-blue-600 to-emerald-500",
            "text-xs font-bold text-white",
            "shadow-lg transition-all",
            "hover:-translate-y-0.5",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-40",
            "disabled:hover:translate-y-0",
            "sm:min-w-48 sm:px-6 sm:text-sm",
          )}
        >
          Continuer
          <ArrowRightIcon aria-hidden size={18} weight="bold" />
        </button>
      )}

      {isFinalization && (
        <button
          type="button"
          disabled={!resume.title?.trim()}
          onClick={() => {
            openDownload("read");
          }}
          className={cn(
            "inline-flex h-11 min-w-40",
            "items-center justify-center gap-3",
            "rounded-xl px-5",
            "bg-gradient-to-r",
            "from-indigo-600 via-blue-600 to-emerald-500",
            "text-xs font-bold text-white",
            "shadow-lg transition-all",
            "hover:-translate-y-0.5",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-40",
            "disabled:hover:translate-y-0",
            "sm:min-w-52 sm:px-6 sm:text-sm",
          )}
        >
          Terminer mon CV
          <ArrowRightIcon aria-hidden size={18} weight="bold" />
        </button>
      )}
    </nav>
  );
};
